import { motion } from "motion/react";
import { ChevronRight, Lock, /*Play,*/ Scan, Star } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { BarcodePattern } from "@/landing/components/BarcodePattern";
import { GooglePlayBadgeLink } from "@/landing/components/GooglePlayBadgeLink";
import { PhoneMockup } from "@/landing/components/PhoneMockup";

export function HeroSection() {
  const { messages, locale } = useI18n();
  const h = messages.hero;

  return (
    <section
      id="hero"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-[#F7F7F5] via-white to-[#F7F7F5] pt-20"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#4CAF50] opacity-10 blur-3xl"
          animate={{
            x: [0, 30, 0],
            y: [0, 50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-[#FFC857] opacity-10 blur-3xl"
          animate={{
            x: [0, -30, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="absolute top-20 left-10 opacity-5">
        <BarcodePattern />
      </div>
      <div className="absolute right-20 bottom-40 rotate-90 opacity-5">
        <BarcodePattern />
      </div>

      <div className="relative z-10 container mx-auto px-6 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-4 inline-block rounded-full bg-[#4CAF50]/10 px-4 py-2"
            >
              <span className="text-sm text-[#4CAF50]">{h.badge}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mb-6 text-5xl tracking-tight text-[#111111] lg:text-7xl"
              style={{ fontFamily: "Inter, system-ui, sans-serif" }}
            >
              {h.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mb-8 max-w-xl text-lg text-gray-600 lg:text-xl"
            >
              {h.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mb-6 flex flex-col gap-4 sm:flex-row"
            >
              <GooglePlayBadgeLink
                locale={locale}
                ariaLabel={h.googlePlay}
                className="inline-flex shrink-0 rounded-lg transition-transform hover:scale-[1.02] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4CAF50]"
                imgClassName="h-14 w-auto sm:h-[60px]"
              />
              {/* <button
                type="button"
                className="flex items-center justify-center gap-2 rounded-2xl border-2 border-gray-200 bg-white px-8 py-4 text-[#111111] transition-colors duration-300 hover:border-[#4CAF50]"
              >
                <Play className="h-5 w-5" />
                <span>{h.watchDemo}</span>
              </button> */}
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex items-center gap-2 text-sm text-gray-500"
            >
              <Lock className="h-4 w-4 shrink-0 text-[#4CAF50]" />
              {h.privacyLine}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="relative flex justify-center lg:justify-end"
          >
            <motion.div
              animate={{
                y: [0, -20, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <PhoneMockup>
                <div className="relative flex h-full flex-col items-center justify-center bg-gradient-to-br from-[#4CAF50] to-[#45a049] p-6">
                  <motion.div
                    className="absolute inset-0 h-32 bg-gradient-to-b from-transparent via-white/30 to-transparent"
                    animate={{
                      y: [0, 400, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />

                  <Scan className="mb-4 h-24 w-24 text-white" />
                  <div className="text-center text-white">
                    <h3 className="mb-2 text-2xl">{h.phoneTitle}</h3>
                    <p className="text-sm text-white/80">{h.phoneSubtitle}</p>
                  </div>

                  <div className="mt-8 rounded-xl bg-white p-4">
                    <BarcodePattern className="text-black" />
                  </div>
                </div>
              </PhoneMockup>

              <motion.div
                className="absolute -left-16 top-20 rounded-xl bg-white p-3 shadow-xl"
                animate={{
                  y: [0, -10, 0],
                  rotate: [-2, 2, -2],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="text-2xl">🍫</div>
                  <div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className="h-3 w-3 fill-[#FFC857] text-[#FFC857]"
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-600">{h.snackCard}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute -right-12 top-40 rounded-xl bg-white p-3 shadow-xl"
                animate={{
                  y: [0, 10, 0],
                  rotate: [2, -2, 2],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="text-2xl">☕</div>
                  <div>
                    <div className="flex gap-0.5">
                      {[1, 2].map((i) => (
                        <Star
                          key={i}
                          className="h-3 w-3 fill-[#FFC857] text-[#FFC857]"
                        />
                      ))}
                      {[3, 4, 5].map((i) => (
                        <Star key={i} className="h-3 w-3 text-gray-300" />
                      ))}
                    </div>
                    <p className="text-xs text-red-600">{h.coffeeCard}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="flex flex-col items-center gap-2 text-gray-400">
          <span className="text-sm">{h.scrollHint}</span>
          <ChevronRight className="h-5 w-5 rotate-90" />
        </div>
      </motion.div>
    </section>
  );
}
